import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import React, { useMemo } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { BuyerGroupingTable } from "@/types/scenario";
import EstimateBadge from "@/pages/Project/Estimate/components/EstimateBadge";
import { ItemSheetExtraTag } from "@/consts/Estimate";
import { DEFAULT_PAGINATION } from "@/consts/common";
import {
  useIsFinancialManager,
  useIsAdmin,
} from "@/hooks/useIsFinancialManager";
import useUserStore from "@/stores/User";
import { CurrentUserContent } from "@/types/setting";

interface Props {
  data: BuyerGroupingTable[];
  canViewMarginRate?: boolean;
  canViewFullFinancialData?: boolean;
  isApprover?: boolean;
}

export default function ApprovalDataTable({
  data,
  canViewMarginRate = false,
  canViewFullFinancialData = false,
  isApprover = false,
}: Props) {
  const storedUser: CurrentUserContent = useUserStore((state) => state.user);
  const isFinancialManager = useIsFinancialManager();
  const isAdmin = useIsAdmin();

  const canViewAllData = isFinancialManager || isAdmin || isApprover;
  const shouldShowTPPOnly = !canViewAllData;

  const totalSummary = useMemo(() => {
    const summary = data.reduce(
      (acc, curr) => {
        acc.totalListPrice += curr.summary.totalListPrice;
        acc.supplyPrice += curr.summary.supplyPrice;

        // 합계는 항상 모든 데이터를 포함해야 함 (화면 표시만 권한에 따라 마스킹)
        acc.totalCost += curr.summary.totalCost;
        acc.margin += curr.summary.margin;

        acc.count += 1;
        return acc;
      },
      {
        totalListPrice: 0,
        supplyPrice: 0,
        totalCost: 0,
        margin: 0,
        count: 0,
        marginRate: 0,
        discountRate: 0,
      }
    );

    const revenue = summary.totalCost + summary.margin;
    summary.marginRate = revenue > 0 ? (summary.margin / revenue) * 100 : 0;

    summary.discountRate =
      summary.totalListPrice > 0
        ? ((summary.totalListPrice - summary.supplyPrice) /
            summary.totalListPrice) *
          100
        : 0;

    return summary;
  }, [data]);

  const columns = useMemo(
    () =>
      createScenarioTableColumns({
        canViewFullFinancialData: canViewAllData || shouldShowTPPOnly,
        canViewMarginRate: canViewAllData || shouldShowTPPOnly,
        shouldShowTPPOnly,
      }),
    [canViewAllData, shouldShowTPPOnly]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pagination: {
        pageSize: DEFAULT_PAGINATION.limit,
      },
    },
  });

  return (
    <div className="border rounded-lg overflow-hidden flex flex-col w-full h-full shadow-lg">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className="px-2 py-[10px] text-sm text-white bg-dark-red-60 first:rounded-tl-md last:rounded-tr-md"
                    style={{
                      minWidth: header.column.columnDef.size,
                      maxWidth: header.column.columnDef.size,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            <>
              {table.getRowModel().rows.map((row) => {
                return (
                  <React.Fragment key={row.id}>
                    <TableRow data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="px-2 py-[10px]"
                          style={{
                            minWidth: cell.column.columnDef.size,
                            maxWidth: cell.column.columnDef.size,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </React.Fragment>
                );
              })}
              {table.getRowModel().rows.length > 0 && (
                <TableRow className="h-14 bg-white font-semibold">
                  <TableCell className="px-2 py-[10px] text-xs text-left">
                    합계
                  </TableCell>
                  <TableCell className="px-2 py-[10px] text-xs text-left">
                    {totalSummary.totalListPrice.toLocaleString()}
                  </TableCell>
                  <TableCell className="px-2 py-[10px] text-xs text-left">
                    {totalSummary.discountRate.toFixed(1)}%
                  </TableCell>
                  <TableCell className="px-2 py-[10px] text-xs text-left">
                    {totalSummary.supplyPrice.toLocaleString()}
                  </TableCell>
                  {(canViewAllData || shouldShowTPPOnly) && (
                    <>
                      <TableCell className="px-2 py-[10px] text-xs text-left">
                        {totalSummary.totalCost.toLocaleString()}
                      </TableCell>
                      <TableCell className="px-2 py-[10px] text-xs text-left">
                        {totalSummary.margin.toLocaleString()}
                      </TableCell>
                    </>
                  )}
                  {(canViewAllData || shouldShowTPPOnly) && (
                    <TableCell className="px-2 py-[10px] text-xs text-left">
                      {totalSummary.marginRate.toFixed(1)}%
                    </TableCell>
                  )}
                </TableRow>
              )}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                데이터가 없습니다.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function createScenarioTableColumns({
  canViewFullFinancialData = false,
  canViewMarginRate = false,
  shouldShowTPPOnly = false,
}: {
  canViewFullFinancialData?: boolean;
  canViewMarginRate?: boolean;
  shouldShowTPPOnly?: boolean;
}): ColumnDef<BuyerGroupingTable>[] {
  const baseColumns: ColumnDef<BuyerGroupingTable>[] = [
    {
      accessorKey: "buyer",
      header: () => <span className="text-sm font-semibold">디비전</span>,
      cell: ({ row }) => (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-gray-A font-normal">
            {row.original.buyer}
          </span>
        </div>
      ),
      size: 144,
    },
    {
      id: "totalListPrice",
      header: () => <span className="text-sm font-semibold">판매가</span>,
      cell: ({ row }) => (
        <span className="text-xs">
          {row.original.summary.totalListPrice.toLocaleString()}
        </span>
      ),
      size: 115,
    },
    {
      accessorKey: "discountRate",
      header: () => <span className="text-sm font-semibold">할인율</span>,
      cell: ({ row }) => {
        return (
          <span className="text-xs">
            {row.original.discountRate?.toFixed(1)}%
          </span>
        );
      },
      size: 115,
    },
    {
      id: "supplyPrice",
      header: () => <span className="text-sm font-semibold">제안가격</span>,
      cell: ({ row }) => (
        <span className="text-xs">
          {row.original.summary.supplyPrice.toLocaleString()}
        </span>
      ),
      size: 115,
    },
    ...(canViewFullFinancialData
      ? [
          {
            id: "totalCost",
            header: () => (
              <span className="text-sm font-semibold">매입가/원가</span>
            ),
            cell: ({ row }: { row: any }) => (
              <span className="text-xs">
                {shouldShowTPPOnly && row.original.buyer !== "TPP"
                  ? ""
                  : row.original.summary.totalCost.toLocaleString()}
              </span>
            ),
            size: 115,
          },
          {
            id: "margin",
            header: () => (
              <span className="text-sm font-semibold">마진금액</span>
            ),
            cell: ({ row }: { row: any }) => (
              <span className="text-xs">
                {shouldShowTPPOnly && row.original.buyer !== "TPP"
                  ? ""
                  : row.original.summary.margin.toLocaleString()}
              </span>
            ),
            size: 115,
          },
        ]
      : []),

    ...(canViewMarginRate
      ? [
          {
            id: "marginRate",
            header: () => <span className="text-sm font-semibold">마진율</span>,
            cell: ({ row }: { row: any }) => (
              <span className="text-xs">
                {shouldShowTPPOnly && row.original.buyer !== "TPP"
                  ? ""
                  : `${row.original.summary.marginRate.toFixed(1)}%`}
              </span>
            ),
            size: 115,
          },
        ]
      : []),
  ];

  return baseColumns;
}
